import type { ProjectCategoryDto } from '@/components/features/project-detail/quotes/types'

/** What a hand-typed line (direct sale, free invoice) is filed under. */
export interface CategoryChoice {
  /** One of the organisation's categories; the server then stores its label. */
  categoryId: string | null
  /** A free label kept from before categories belonged to the organisation (no id). */
  categoryLabel: string | null
}

export const NO_CATEGORY: CategoryChoice = { categoryId: null, categoryLabel: null }

export interface CategoryOption {
  value: string
  label: string
}

export const NONE_VALUE = '__none__'
export const LEGACY_VALUE = '__legacy__'
export const NEW_VALUE = '__new__'

/**
 * The options of the category picker: none, the organisation's visible categories (plus the hidden
 * one the line already uses), and the free label an older line carries, so saving it unchanged
 * does not drop it.
 */
export function categoryOptions(categories: readonly ProjectCategoryDto[], value: CategoryChoice): CategoryOption[] {
  const options: CategoryOption[] = [{ value: NONE_VALUE, label: 'Aucune' }]
  for (const c of categories) {
    if (!c.isHidden || c.id === value.categoryId) options.push({ value: c.id, label: c.label })
  }
  if (!value.categoryId && value.categoryLabel) {
    options.push({ value: LEGACY_VALUE, label: `${value.categoryLabel} (ancien libellé)` })
  }
  return options
}

/** The select value for a choice. */
export function selectValueOf(value: CategoryChoice): string {
  if (value.categoryId) return value.categoryId
  return value.categoryLabel ? LEGACY_VALUE : NONE_VALUE
}

/** The choice a selected option stands for; the legacy option keeps the line's free label. */
export function choiceOf(selected: string, categories: readonly ProjectCategoryDto[], current: CategoryChoice): CategoryChoice {
  if (selected === NONE_VALUE) return NO_CATEGORY
  if (selected === LEGACY_VALUE) return { categoryId: null, categoryLabel: current.categoryLabel }
  const category = categories.find((c) => c.id === selected)
  return { categoryId: selected, categoryLabel: category?.label ?? null }
}
